import * as React from "react"
import ReactDOM from "react-dom"
import { SvgDownload01 } from "@digits-shared/components/SVGIcons/line/Download01.svg"
import { type ButtonSize, DigitsButton } from "@digits-shared/DesignSystem/Button"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import { useWaitForElement } from "@digits-shared/hooks/useWaitForElement"
import styled from "styled-components"
import { isAgingReportKind } from "src/frontend/components/OS/Applications/Reports/Packages/shared"
import { useReportDocumentOptions } from "src/frontend/components/OS/Applications/Reports/Report/hooks/useReportDocumentOptions"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import { mapAgingReportRows } from "src/frontend/components/Shared/Layout/Components/Statements/Excel/mapAgingReportRows"
import { useExportToExcel } from "src/frontend/components/Shared/Layout/Components/Statements/Excel/useExportToExcel"
import { useComponentTitle } from "src/frontend/components/Shared/Layout/Components/useComponentTitle"
import type { MatchedComponent, MatchedConfig } from "src/frontend/components/Shared/Layout/types"
import { useStopClickPropagation } from "src/shared/hooks/useStopClickPropagation"

const HidingControls = styled.div`
  display: contents;
  @media print {
    & {
      display: none;
    }
  }
`

/*
  INTERFACES
*/

interface ExportExcelProps {
  layoutId: string | undefined | null
  sectionId?: string
  component: MatchedComponent<"statement">
  configOverride?: MatchedConfig<"statement">
  $circle: boolean
  size?: ButtonSize
  disabled?: boolean
}

interface ButtonProps {
  exportExcel: () => Promise<void>
  $circle: boolean
  size?: ButtonSize
}

interface PortalProps extends ButtonProps {
  sectionId: string
}

/*
  COMPONENTS
*/

export const ExportExcelButton: React.FC<ExportExcelProps> = ({
  sectionId,
  layoutId,
  component,
  configOverride,
  size,
  $circle,
  disabled,
}) => {
  const config = configOverride ?? component.config
  const { dataId } = component
  const { title, periodName } = useComponentTitle(component)
  const viewId = useViewVersion()
  const isAging = isAgingReportKind(config.statement.kind)
  const options = useReportDocumentOptions()

  const exportExcel = useExportToExcel({
    title,
    periodName,
    config,
    layoutId,
    dataId,
    viewId,
    options,
    mapRowsFn: isAging ? mapAgingReportRows : undefined,
  })

  if (disabled) return null

  return (
    <HidingControls>
      {sectionId && (
        <ButtonPortal
          sectionId={sectionId}
          exportExcel={exportExcel}
          size={size}
          $circle={$circle}
        />
      )}
      {!sectionId && <ExportButton exportExcel={exportExcel} size={size} $circle={$circle} />}
    </HidingControls>
  )
}

const ButtonPortal: React.FC<PortalProps> = ({ sectionId, exportExcel, size }) => {
  const statementMenu = useWaitForElement(`statement-action-menu-${sectionId}`)
  if (!statementMenu) return null
  return (
    <>
      {ReactDOM.createPortal(
        <ExportButton exportExcel={exportExcel} size={size} $circle />,
        statementMenu
      )}
    </>
  )
}

const ExportButton: React.FC<ButtonProps> = ({ exportExcel, size, $circle }) => {
  const stopPropagation = useStopClickPropagation()

  const onExportClick = React.useCallback(
    (e: React.MouseEvent) => {
      stopPropagation(e)
      return exportExcel()
    },
    [exportExcel, stopPropagation]
  )

  return (
    <DigitsTooltip content="Download XLS" disabled={!$circle}>
      <DigitsButton
        size={size || "small"}
        $variant="secondary-dark"
        onClick={onExportClick}
        $circle={$circle}
      >
        <SvgDownload01 />
        {!$circle && <>Download</>}
      </DigitsButton>
    </DigitsTooltip>
  )
}
